import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, errText } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, KeyRound } from "lucide-react";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== confirm) return toast.error("Passwords don't match");
    if (!token) return toast.error("Missing reset token");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/auth"), 2500);
    } catch (err) {
      toast.error(errText(err, "Could not reset password"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#F6F5F1]">
      <div className="max-w-md w-full">
        <div className="text-center mb-10">
          <div className="font-mono-eyebrow text-black/50">A fresh start</div>
          <h1 className="font-serif-display text-5xl leading-none mt-2">Set a new password.</h1>
        </div>
        {done ? (
          <div data-testid="reset-done" className="bg-white border border-black/10 p-8 text-center">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-4 text-green-700"/>
            <p className="font-serif-display text-2xl">Password reset.</p>
            <p className="text-black/60 mt-2 text-sm">Redirecting you back to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-black/10 p-8 space-y-6">
            <div className="flex items-start gap-3 pb-4 border-b border-black/10">
              <KeyRound className="w-5 h-5 mt-0.5 text-black/60"/>
              <p className="text-black/60 text-sm leading-relaxed">
                Pick a new password (at least 8 characters). This link is
                single-use and expires 30 minutes after it was sent.
              </p>
            </div>
            <div>
              <label className="font-mono-eyebrow">New password</label>
              <input
                data-testid="reset-pw"
                required type="password"
                value={pw} onChange={(e) => setPw(e.target.value)}
                className="field mt-2" placeholder="••••••••" minLength={8}
              />
            </div>
            <div>
              <label className="font-mono-eyebrow">Confirm password</label>
              <input
                data-testid="reset-pw-confirm"
                required type="password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="field mt-2" placeholder="••••••••" minLength={8}
              />
            </div>
            <button data-testid="reset-submit" type="submit" disabled={busy} className="btn-ink w-full disabled:opacity-50">
              {busy ? "Saving…" : "Reset password"}
            </button>
            <div className="text-center pt-4 border-t border-black/10">
              <Link to="/auth" className="editorial-link font-mono-eyebrow">Cancel</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
